import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import Plotly from 'plotly.js-dist';
import './frameworkvisualizer.css';

// Framework configuration - EXACTLY matches Python code and framework file
const splits = [4, 4, 4, 3, 8, 8, 6, 6, 11, 11, 7, 4];  // 12 sections, 76 total sub-elements

const section_names = [
  'Outcomes',
  'Objectives', 
  'Outputs',
  'Functions',
  'Threats',
  'Opportunities',
  'Strategy',
  'Governance and Organization',
  'Financing',
  'Resource Management',
  'Health Services',
  'Implementation'
];

// Framework definitions
const FRAMEWORK_DEFINITIONS = {
  'Outcomes': 'How well is the health system performing in relation to cancer care and control concerning health system outcomes?',
  'Objectives': 'How well is the health system producing health services for cancer in terms of the four value dimensions?',
  'Outputs': 'Is the health system delivering the right health services to respond to cancer?',
  'Functions': 'How are health system inputs such as governance and organisation, financing, and resource management currently generated and used in health systems to deliver public and individual health services for cancer control?',
  'Threats': 'What contextual threats will influence cancer demands on the health system and influence the system response to cancer care and control?',
  'Opportunities': 'What contextual opportunities will influence cancer demands on the health system and influence the system response to cancer care and control?',
  'Strategy': 'How will the challenge identified be addressed to improve health system performance and create value in relation to cancer?',
  'Governance and Organization': 'What reforms are to be undertaken in terms of governance and organization of the health system to achieve the articulated strategy to improve cancer care and control?',
  'Financing': 'What reforms are to be undertaken in terms of financing of the health system to achieve the articulated strategy to improve cancer care and control?',
  'Resource Management': 'What reforms are to be undertaken in terms of resource management of the health system to achieve the articulated strategy to improve cancer care and control?',
  'Health Services': 'What reforms are to be undertaken in terms of the delivery of health services to achieve the articulated strategy to improve cancer care and control?',
  'Implementation': 'How will the plan be implemented, measured, stakeholder alignment and sustained changed management achieved?'
};

// Max scores dictionary - CORRECTED based on NCCPFramework_Aug9.txt scoring definitions
const max_scores_dict = {
  // Outcomes (4)
  "Health": 5, 
  "Financial Risk Protection": 5, 
  "User Satisfaction": 5,
  "Psychosocial Support Services": 5,
  
  // Objectives (4)
  "Effectiveness": 5,
  "Efficiency": 4, 
  "Equity": 4, 
  "Responsiveness": 4,
  
  // Outputs (4)
  "Individual Health Services": 5,
  "Population Health Services": 4,
  "Community-Based Palliative Care": 5,
  "Cancer Surveillance Systems": 5,
  
  // Functions (3)
  "Governance and Organisation": 5, 
  "Financing": 5, 
  "Resource Management": 5,
  
  // Threats (8)
  "Demographic Threats": 4, 
  "Epidemiologic Threats": 4, 
  "Political Threats": 4, 
  "Legal Threats": 4,
  "Sociocultural Threats": 4, 
  "Economic Threats": 4, 
  "Ecological Threats": 4, 
  "Technological Threats": 4,
  
  // Opportunities (8)
  "Demographic Opportunities": 4, 
  "Epidemiologic Opportunities": 4, 
  "Political Opportunities": 4,
  "Legal Opportunities": 4, 
  "Sociocultural Opportunities": 4, 
  "Economic Opportunities": 4,
  "Ecological Opportunities": 4, 
  "Technological Opportunities": 4,
  
  // Strategy (6)
  "Vision": 3,
  "Mission": 3,
  "Goals": 3, 
  "Objectives": 3, 
  "Values": 2,
  "Integration with Noncommunicable Disease Programs": 5,
  
  // Governance and Organization (6)
  "Macro-organization": 2, 
  "Governance": 5, 
  "Policy": 5, 
  "Regulation": 5,
  "Decentralization": 2, 
  "Strategic public private partnerships": 5,
  
  // Financing (11)
  "Cost measurement systems": 3, 
  "Current financing and fiscal space": 5,
  "Proposed funding to implement plan": 3, 
  "Sources of funds": 5,
  "Resource Mobilization Strategies": 5,
  "Financing for Governance and Oversight": 5,
  "Pooling of funds": 2, 
  "Channeling of funds": 2, 
  "Allocation of funds": 4,
  "Payment mechanisms for providers": 4, 
  "Payment mechanisms for capital investments": 3,
  
  // Resource Management (11)
  "Human resources": 5, 
  "Capacity Building for Health Professionals": 5,
  "Infrastructure": 5,
  "Pharmaceuticals and medical supplies": 5,
  "Patient Navigation": 5,
  "Information technology and data systems": 5, 
  "Supply chain management": 3,
  "Knowledge Generation": 3,
  "Innovation ecosystem": 3, 
  "Education Initiatives": 5,
  "Innovation Initiatives": 5,
  
  // Health Services (7)
  "Public Health Services (Health promotion)": 5, 
  "Public Health Services (Health protection)": 5,
  "Public Health Services (Disease Prevention)": 5, 
  "Personal Healthcare Services (Diagnosis)": 4,
  "Personal Healthcare Services (Treatment)": 4,
  "Personal Healthcare Services (Palliation and rehabilitative care)": 4,
  "Provider Value Enhancement": 3,
  
  // Implementation (4)
  "Monitoring and Evaluation framework": 2,
  "Change management": 2,
  "Risk and Mitigation Strategies": 3,
  "Stakeholder engagement": 3
};

// PDF filename mapping for countries with special naming conventions
const PDF_FILENAME_MAP = {
  'New Zealand': 'New-Zealand',
  'South Africa': 'South_Africa',
  'Sri Lanka': 'Sri_Lanka',
  'United Kingdom': 'United_Kingdom'
};

// Function to get possible PDF paths for a country (tries multiple patterns)
const getPossiblePdfPaths = (country, year) => {
  let filePrefix = PDF_FILENAME_MAP[country] || country.replace(/ /g, '_');
  
  // Return array of possible paths to try
  return [
    `/data/Commonwealth_PDFS/${filePrefix}_${year}.pdf`,
    `/data/Commonwealth_PDFS/${filePrefix}_${year}_FINAL.pdf`,
    `/data/Commonwealth_PDFS/${filePrefix}_${year}_FL.pdf`,
  ];
};

// Build sub-element to section mapping
const buildSubElementToSection = () => {
  const mapping = {};
  const subElementKeys = Object.keys(max_scores_dict);
  let cumulativeIndex = 0;

  splits.forEach((splitCount, sectionIndex) => {
    const sectionName = section_names[sectionIndex];
    for (let i = 0; i < splitCount; i++) {
      const subElement = subElementKeys[cumulativeIndex + i];
      if (subElement) {
        mapping[subElement] = sectionName;
      }
    }
    cumulativeIndex += splitCount;
  });

  return mapping;
};

const SUBELEMENT_TO_SECTION = buildSubElementToSection();

// Get sub-elements for a specific section
const getSubElementsForSection = (sectionName) => {
  return Object.entries(SUBELEMENT_TO_SECTION)
    .filter(([_, section]) => section === sectionName)
    .map(([subElement, _]) => subElement);
};

// Parse page citations and make them clickable
const PageCitations = ({ citations, onPageClick }) => {
  if (!citations) return null;
  
  // Parse citations like "7, 8, 9" or "7-9" or "7, 8, 9, 10-15"
  const pages = citations.split(',').map(p => p.trim()).filter(p => p);
  
  console.log('[PageCitations] Rendering citations:', citations, 'parsed pages:', pages);
  
  return (
    <div className="fw-modal-citations">
      <span className="fw-modal-citations-label">Pages: </span>
      {pages.map((page, index) => (
        <span key={index}>
          <button
            className="fw-modal-page-link"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('[PageCitations] Button clicked for page:', page);
              onPageClick(page);
            }}
            title={`Go to page ${page}`}
          >
            {page}
          </button>
          {index < pages.length - 1 && ', '}
        </span>
      ))}
    </div>
  );
};

// Searchable Single-Select Component
const SearchableSingleSelect = ({ options, selected, onSelect, placeholder, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(opt => 
    opt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayText = selected ? selected : placeholder;

  return (
    <div className="fw-dropdown-container" id={id} ref={dropdownRef}>
      <div className="fw-dropdown-header" onClick={() => setIsOpen(!isOpen)}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={displayText}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
          className="fw-dropdown-search-input"
        />
        <span className="fw-dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div className="fw-dropdown-menu">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, index) => (
              <div
                key={index}
                className={`fw-dropdown-item ${selected === opt ? 'selected' : ''}`}
                onClick={() => {
                  onSelect(opt);
                  setIsOpen(false);
                  setSearchTerm('');
                }}
              >
                <span>{opt}</span>
              </div>
            ))
          ) : (
            <div className="fw-dropdown-item no-options">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

// PDF Viewer Component with fallback and page navigation
const PdfViewer = ({ country, year, targetPage }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    const findValidPdf = async () => {
      setLoading(true);
      setError(false);
      
      const possiblePaths = getPossiblePdfPaths(country, year);
      
      for (const path of possiblePaths) {
        try {
          const response = await fetch(path, { method: 'HEAD' });
          if (response.ok) {
            const contentType = response.headers.get('content-type');
            // Make sure it's actually a PDF and not an HTML page
            if (contentType && contentType.includes('application/pdf')) {
              console.log('[PdfViewer] Found valid PDF:', path);
              setPdfUrl(path);
              setLoading(false);
              return;
            }
          }
        } catch (e) {
          // Continue to next path
        }
      }
      
      // No valid PDF found
      setError(true);
      setLoading(false);
      setPdfUrl(possiblePaths[0]); // Set first path for download link
    };

    findValidPdf();
  }, [country, year]);

  // Navigate to page when targetPage changes
  useEffect(() => {
    console.log('[PdfViewer] targetPage changed:', targetPage);
    
    if (targetPage?.page && pdfUrl) {
      // Extract the first number from page reference (handles "7-9" format)
      const pageMatch = targetPage.page.toString().match(/\d+/);
      console.log('[PdfViewer] pageMatch:', pageMatch);
      
      if (pageMatch) {
        const page = parseInt(pageMatch[0], 10);
        console.log('[PdfViewer] Navigating to page:', page);
        setCurrentPage(page);
      }
    }
  }, [targetPage, pdfUrl]);

  // Build the full URL with page parameter
  const getFullPdfUrl = () => {
    if (!pdfUrl) return null;
    if (currentPage) {
      return `${pdfUrl}#page=${currentPage}`;
    }
    return pdfUrl;
  };

  if (loading) {
    return (
      <div className="fw-modal-pdf-loading">
        <div className="fw-loading-spinner"></div>
        <p>Loading PDF...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fw-modal-pdf-error">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
        <p>PDF not found for {country} ({year})</p>
        <p className="fw-modal-pdf-error-hint">
          The PDF file may not be available or may have a different name.
        </p>
        <a 
          href={pdfUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="fw-modal-pdf-fallback-link"
        >
          Try opening directly
        </a>
      </div>
    );
  }

  const fullUrl = getFullPdfUrl();
  console.log('[PdfViewer] Rendering iframe with URL:', fullUrl);

  return (
    <iframe
      key={currentPage || 'initial'} // Force re-render on page change
      ref={iframeRef}
      src={fullUrl}
      title={`${country} Cancer Control Plan`}
      className="fw-modal-pdf-iframe"
    />
  );
};

// Section Analysis Modal Component with PDF Viewer
const SectionAnalysisModal = ({ section, subElements, countryData, maxScores, onClose, country, year }) => {
  const [targetPage, setTargetPage] = useState(null);
  
  if (!section) return null;

  const handlePageClick = (page) => {
    console.log('[SectionAnalysisModal] Page clicked:', page);
    // Use timestamp to ensure state change even if same page clicked twice
    setTargetPage({ page, timestamp: Date.now() });
  };

  const sectionSubElements = subElements.map(subElement => {
    const dataRow = countryData.find(row => row.subElement === subElement);
    const maxScore = maxScores[subElement] || 5;
    return {
      subElement,
      score: dataRow?.score || 0,
      maxScore,
      normalized: dataRow ? (dataRow.score / maxScore) * 5 : 0,
      response: dataRow?.response || 'No data available',
      reasoning: dataRow?.reasoning || 'No reasoning available',
      pageCitations: dataRow?.pageCitations || '',
      reasoningCitations: dataRow?.reasoningCitations || ''
    };
  });

  const avgScore = sectionSubElements.length > 0
    ? sectionSubElements.reduce((sum, item) => sum + item.normalized, 0) / sectionSubElements.length
    : 0;

  return (
    <div 
      className="fw-modal-overlay"
      onClick={onClose}
    >
      <div 
        className="fw-modal-content fw-modal-split"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left Panel - Analysis */}
        <div className="fw-modal-analysis-panel">
          <div className="fw-modal-header">
            <h2>{section}</h2>
          </div>
          
          <div className="fw-modal-definition">
            <p>{FRAMEWORK_DEFINITIONS[section]}</p>
          </div>

          <div className="fw-modal-score-summary">
            <span className="fw-modal-score-label">Section Average Score:</span>
            <span 
              className="fw-modal-score-value"
              style={{
                color: avgScore >= 3.5 ? '#10B981' : avgScore >= 2 ? '#F59E0B' : '#EF4444'
              }}
            >
              {avgScore.toFixed(2)} / 5.00
            </span>
          </div>

          <div className="fw-modal-subelements">
            <h3>Sub-Elements</h3>
            <div className="fw-modal-subelements-list">
              {sectionSubElements.map((item, index) => {
                const scoreColor = item.normalized >= 3.5 ? '#10B981' : item.normalized >= 2 ? '#F59E0B' : '#EF4444';
                const scoreBgColor = item.normalized >= 3.5 ? '#D1FAE5' : item.normalized >= 2 ? '#FEF3C7' : '#FEE2E2';
                
                return (
                  <div key={index} className="fw-modal-subelement-card">
                    <div className="fw-modal-subelement-header">
                      <span className="fw-modal-subelement-name">{item.subElement}</span>
                      <span 
                        className="fw-modal-subelement-score"
                        style={{ backgroundColor: scoreBgColor, color: scoreColor }}
                      >
                        {item.normalized.toFixed(1)} / 5.0
                      </span>
                    </div>
                    <div className="fw-modal-subelement-details">
                      <div className="fw-modal-detail-section">
                        <strong>Response:</strong>
                        <p>{item.response}</p>
                        <PageCitations citations={item.pageCitations} onPageClick={handlePageClick} />
                      </div>
                      <div className="fw-modal-detail-section">
                        <strong>Reasoning:</strong>
                        <p>{item.reasoning}</p>
                        <PageCitations citations={item.reasoningCitations} onPageClick={handlePageClick} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Panel - PDF Viewer */}
        <div className="fw-modal-pdf-panel">
          <div className="fw-modal-pdf-header">
            <h3>{country} Cancer Control Plan</h3>
            <div className="fw-modal-pdf-actions">
              <a 
                href={getPossiblePdfPaths(country, year)[0]} 
                target="_blank" 
                rel="noopener noreferrer"
                className="fw-modal-pdf-download"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                Open in New Tab
              </a>
              <button className="fw-modal-close" onClick={onClose} title="Close (or click outside)">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
          <div className="fw-modal-pdf-container">
            <PdfViewer 
              country={country} 
              year={year} 
              targetPage={targetPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Sub-Element Detail Modal Component with PDF Viewer
const SubElementDetailModal = ({ subElement, countryData, maxScores, onClose, country, year }) => {
  const [targetPage, setTargetPage] = useState(null);
  
  if (!subElement) return null;

  const handlePageClick = (page) => {
    console.log('[SubElementDetailModal] Page clicked:', page);
    // Use timestamp to ensure state change even if same page clicked twice
    setTargetPage({ page, timestamp: Date.now() });
  };

  const dataRow = countryData.find(row => row.subElement === subElement);
  const maxScore = maxScores[subElement] || 5;
  const normalizedScore = dataRow ? (dataRow.score / maxScore) * 5 : 0;
  const section = SUBELEMENT_TO_SECTION[subElement];

  const scoreColor = normalizedScore >= 3.5 ? '#10B981' : normalizedScore >= 2 ? '#F59E0B' : '#EF4444';

  return (
    <div 
      className="fw-modal-overlay"
      onClick={onClose}
    >
      <div 
        className="fw-modal-content fw-modal-split"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left Panel - Analysis */}
        <div className="fw-modal-analysis-panel">
          <div className="fw-modal-header">
            <h2>{subElement}</h2>
          </div>
          
          <div className="fw-modal-definition">
            <p><strong>Section:</strong> {section}</p>
          </div>

          <div className="fw-modal-score-summary">
            <span className="fw-modal-score-label">Score:</span>
            <span 
              className="fw-modal-score-value"
              style={{ color: scoreColor }}
            >
              {normalizedScore.toFixed(2)} / 5.00
            </span>
            <span style={{ fontSize: '0.875rem', color: '#6B7280', marginLeft: '0.5rem' }}>
              (Raw: {dataRow?.score || 0} / {maxScore})
            </span>
          </div>

          <div className="fw-modal-subelements">
            <div className="fw-modal-subelement-card">
              <div className="fw-modal-subelement-details">
                <div className="fw-modal-detail-section">
                  <strong>Response:</strong>
                  <p>{dataRow?.response || 'No data available'}</p>
                  <PageCitations citations={dataRow?.pageCitations} onPageClick={handlePageClick} />
                </div>
                <div className="fw-modal-detail-section">
                  <strong>Reasoning:</strong>
                  <p>{dataRow?.reasoning || 'No reasoning available'}</p>
                  <PageCitations citations={dataRow?.reasoningCitations} onPageClick={handlePageClick} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - PDF Viewer */}
        <div className="fw-modal-pdf-panel">
          <div className="fw-modal-pdf-header">
            <h3>{country} Cancer Control Plan</h3>
            <div className="fw-modal-pdf-actions">
              <a 
                href={getPossiblePdfPaths(country, year)[0]} 
                target="_blank" 
                rel="noopener noreferrer"
                className="fw-modal-pdf-download"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                Open in New Tab
              </a>
              <button className="fw-modal-close" onClick={onClose} title="Close (or click outside)">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
          <div className="fw-modal-pdf-container">
            <PdfViewer 
              country={country} 
              year={year} 
              targetPage={targetPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const FrameworkExplorer = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countryYearMap, setCountryYearMap] = useState({});
  
  const [selectedCountry, setSelectedCountry] = useState('United Kingdom');
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedSubElement, setSelectedSubElement] = useState(null);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showSubElementModal, setShowSubElementModal] = useState(false);
  
  const [countryData, setCountryData] = useState([]);
  const [averagedScores, setAveragedScores] = useState([]);
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  
  const chartRef = useRef(null);

  // Get available sub-elements based on selected section
  const availableSubElements = selectedSection 
    ? getSubElementsForSection(selectedSection) 
    : Object.keys(max_scores_dict);

  // Color palette for sections
  const sectionColors = {
    'Outcomes': '#06B6D4',
    'Objectives': '#FBBF24',
    'Outputs': '#A78BFA',
    'Functions': '#3B82F6',
    'Threats': '#EC4899',
    'Opportunities': '#9CA3AF',
    'Strategy': '#FB923C',
    'Governance and Organization': '#60A5FA',
    'Financing': '#38BDF8',
    'Resource Management': '#22D3EE',
    'Health Services': '#F97316',
    'Implementation': '#2DD4BF'
  };

  // Load CSV data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const response = await fetch('/data/CW_nccp_analyses.csv');
        
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        
        const csvText = await response.text();
        
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const cleanedData = results.data
              .filter(row => row.country && row['Sub-Element'] && row.score)
              .map(row => ({
                country: row.country.trim(),
                year: parseInt(row.year),
                subElement: row['Sub-Element'].trim(),
                response: row.response || '',
                score: parseFloat(row.score),
                reasoning: row.reasoning || '',
                pageCitations: row.response_page_citations || '',
                reasoningCitations: row.scoring_reasoning_page_citations || ''
              }))
              .filter(row => !isNaN(row.score));

            // Get unique countries (no filtering needed - CSV already has correct countries)
            const uniqueCountries = [...new Set(cleanedData.map(row => row.country))].sort();
            
            // Build country to year mapping
            const yearMap = {};
            cleanedData.forEach(row => {
              if (!yearMap[row.country]) {
                yearMap[row.country] = row.year;
              }
            });
            
            setData(cleanedData);
            setCountries(uniqueCountries);
            setCountryYearMap(yearMap);
            setLoading(false);
          },
          error: (error) => {
            console.error('Error parsing CSV:', error);
            setLoading(false);
          }
        });
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter data when country changes
  useEffect(() => {
    if (data.length === 0 || !selectedCountry) return;

    const filtered = data.filter(row => row.country === selectedCountry);
    setCountryData(filtered);

    // Calculate averaged scores
    const averaged = calculateAveragedScores(filtered);
    setAveragedScores(averaged);
  }, [data, selectedCountry]);

  // Calculate averaged and normalized scores by section
  const calculateAveragedScores = (countryData) => {
    const subElementKeys = Object.keys(max_scores_dict);
    const scores = [];
    let index = 0;

    splits.forEach((count, sectionIndex) => {
      const sectionScores = [];
      
      for (let i = 0; i < count; i++) {
        const subElement = subElementKeys[index + i];
        const dataRow = countryData.find(row => row.subElement === subElement);
        
        if (dataRow && !isNaN(dataRow.score)) {
          const maxScore = max_scores_dict[subElement] || 5;
          const normalized = (dataRow.score / maxScore) * 5;
          sectionScores.push(normalized);
        }
      }
      
      const avg = sectionScores.length > 0 
        ? sectionScores.reduce((a, b) => a + b, 0) / sectionScores.length 
        : 0;
      
      scores.push(avg);
      index += count;
    });

    return scores;
  };

  // Handle section click - opens modal
  const handleSectionClick = (section) => {
    setSelectedSection(section);
    setShowSectionModal(true);
  };

  // Handle section selection from dropdown (doesn't open modal)
  const handleSectionSelect = (section) => {
    setSelectedSection(section);
    setSelectedSubElement(null); // Reset sub-element when section changes
  };

  // Handle sub-element selection from dropdown
  const handleSubElementSelect = (subElement) => {
    setSelectedSubElement(subElement);
    setShowSubElementModal(true);
  };

  // Render radial chart
  useEffect(() => {
    if (!chartRef.current || averagedScores.length === 0) return;

    const colors = section_names.map(name => sectionColors[name]);

    const traceData = [{
      type: 'barpolar',
      r: averagedScores,
      theta: section_names,
      marker: {
        color: colors,
        line: {
          color: 'white',
          width: 2
        }
      },
      hovertemplate: '<b>%{theta}</b><br>Score: %{r:.2f} / 5.0<extra></extra>'
    }];

    const layout = {
      polar: {
        radialaxis: {
          visible: true,
          range: [0, 5],
          tickvals: [0, 1, 2, 3, 4, 5],
          ticktext: ['0', '1', '2', '3', '4', '5'],
          tickfont: { size: 11, color: '#6B7280' },
          gridcolor: '#E5E7EB',
          showline: false
        },
        angularaxis: {
          tickfont: { size: 10, weight: 'bold', color: '#1F2937' },
          direction: 'clockwise',
          rotation: 90,
          gridcolor: '#E5E7EB'
        },
        bgcolor: 'white',
        bargap: 0.1
      },
      showlegend: false,
      paper_bgcolor: 'white',
      plot_bgcolor: 'white',
      margin: { t: 40, b: 40, l: 80, r: 80 },
      height: 550,
      autosize: true
    };

    const config = {
      responsive: true,
      displayModeBar: false,
      displaylogo: false
    };

    Plotly.newPlot(chartRef.current, traceData, layout, config);

    // Add click event for sections on the chart
    chartRef.current.on('plotly_click', (eventData) => {
      if (eventData.points && eventData.points.length > 0) {
        const section = eventData.points[0].theta;
        handleSectionClick(section);
      }
    });

    return () => {
      if (chartRef.current) {
        Plotly.purge(chartRef.current);
      }
    };
  }, [averagedScores, selectedCountry]);

  // Calculate overall score
  const overallScore = averagedScores.length > 0 
    ? (averagedScores.reduce((a, b) => a + b, 0) / averagedScores.length).toFixed(2)
    : '0.00';

  // Get current country's year
  const currentYear = countryYearMap[selectedCountry] || 2023;

  if (loading) {
    return (
      <div className="fw-loading-container">
        <div className="fw-loading-spinner"></div>
        <div className="fw-loading-message">Loading Framework Data...</div>
      </div>
    );
  }

  return (
    <div 
      className="fw-explorer-container"
      data-sidebar-open={isSidebarOpen.toString()}
    >
      {/* Sidebar */}
      <div className={`fw-sidebar ${isSidebarOpen ? 'open' : 'collapsed'}`}>
        {isSidebarOpen && (
          <div className="fw-sidebar-content">
            <div className="fw-sidebar-header">
              <h2>NCCP Framework Analysis</h2>
              <p className="fw-sidebar-subtitle">National Cancer Control Plans</p>
            </div>

            {/* Country Selector */}
            <div className="fw-section">
              <h3 className="fw-section-title">Country</h3>
              <SearchableSingleSelect
                options={countries}
                selected={selectedCountry}
                onSelect={(country) => {
                  setSelectedCountry(country);
                  setSelectedSection(null);
                  setSelectedSubElement(null);
                }}
                placeholder="Select Country"
                id="country-select"
              />
            </div>

            {/* Section Selector */}
            <div className="fw-section">
              <h3 className="fw-section-title">Section (Element)</h3>
              <SearchableSingleSelect
                options={section_names}
                selected={selectedSection}
                onSelect={handleSectionSelect}
                placeholder="Select Section"
                id="section-select"
              />
            </div>

            {/* Sub-Element Selector */}
            <div className="fw-section">
              <h3 className="fw-section-title">Sub-Element</h3>
              <SearchableSingleSelect
                options={availableSubElements}
                selected={selectedSubElement}
                onSelect={handleSubElementSelect}
                placeholder={selectedSection ? "Select Sub-Element" : "Select a Section first"}
                id="subelement-select"
              />
            </div>

            {/* Overall Score Card */}
            <div className="fw-score-card">
              <div className="fw-score-label">Overall Score</div>
              <div className="fw-score-value">{overallScore}</div>
              <div className="fw-score-max">/ 5.00</div>
              <div className="fw-score-bar">
                <div 
                  className="fw-score-bar-fill"
                  style={{ width: `${(parseFloat(overallScore) / 5) * 100}%` }}
                />
              </div>
            </div>

            {/* Instructions */}
            <div className="fw-instructions">
              <p>Click on chart sections or legend items to view detailed analysis. Click page numbers to navigate the PDF.</p>
            </div>
          </div>
        )}
        
        {/* Sidebar Toggle */}
        <button 
          className="fw-sidebar-toggle"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          {isSidebarOpen ? '◀' : '▶'}
        </button>
      </div>

      {/* Main Content */}
      <div className="fw-main-content">
        {/* Header */}
        <div className="fw-header">
          <div className="fw-header-info">
            <h1 className="fw-header-title">
              {selectedCountry} - Framework Analysis
            </h1>
            <p className="fw-header-subtitle">
              Click on chart sections or legend items to explore sub-elements with source document
            </p>
          </div>
        </div>

        {/* Content Area */}
        <div className="fw-content-area">
          {/* Radar Chart with Legend Side by Side */}
          <div className="fw-chart-with-legend">
            {/* Radial Chart */}
            <div className="fw-chart-container">
              <div 
                ref={chartRef}
                className="fw-radial-chart"
              />
            </div>

            {/* Section Legend - Right side of radar */}
            <div className="fw-legend-panel">
              <h3 className="fw-legend-panel-title">SECTIONS</h3>
              <div className="fw-legend-items-vertical">
                {section_names.map((name, index) => (
                  <div 
                    key={name}
                    className={`fw-legend-item-horizontal ${selectedSection === name ? 'active' : ''}`}
                    onClick={() => handleSectionClick(name)}
                  >
                    <div 
                      className="fw-legend-color-box"
                      style={{ backgroundColor: sectionColors[name] }}
                    />
                    <span className="fw-legend-text">{name}</span>
                    <span className="fw-legend-score-badge">
                      {averagedScores[index]?.toFixed(1) || '0.0'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Analysis Modal */}
      {showSectionModal && selectedSection && (
        <SectionAnalysisModal
          section={selectedSection}
          subElements={getSubElementsForSection(selectedSection)}
          countryData={countryData}
          maxScores={max_scores_dict}
          country={selectedCountry}
          year={currentYear}
          onClose={() => {
            setShowSectionModal(false);
          }}
        />
      )}

      {/* Sub-Element Detail Modal */}
      {showSubElementModal && selectedSubElement && (
        <SubElementDetailModal
          subElement={selectedSubElement}
          countryData={countryData}
          maxScores={max_scores_dict}
          country={selectedCountry}
          year={currentYear}
          onClose={() => {
            setShowSubElementModal(false);
            setSelectedSubElement(null);
          }}
        />
      )}
    </div>
  );
};

export default FrameworkExplorer;